import React, { useCallback, useMemo } from 'react'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStickyFooter from '../../OfferPageStickyFooter'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import { getOccupancyDescription } from 'components/SearchV2/Utils/GlobalSearchV2'
import { pluralizeToString } from 'lib/string/pluralize'
import FlashHotelAvailabilityButton from './FlashHotelAvailabilityButton'

interface Props {
  offer: App.Offer;
  rooms: Array<App.RoomOccupants>;
  checkIn: moment.Moment;
  duration: number;
  lowestPricePackage: App.Package | App.BundlePackageWithPrice;
}

function HotelOfferPageStickyFooterVariant(props: Props) {
  const {
    offer,
    rooms,
    checkIn,
    duration,
    lowestPricePackage,
  } = props

  const offerPageDispatch = useOfferPageDispatch()

  const pricePackage = useOfferPackagePrice(lowestPricePackage, offer)
  const shouldShowRate = pricePackage.valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowValue = getHotelOfferDisplayedValue(!!offer.bundledWithFlightsOnly, pricePackage.totalPrice, pricePackage.totalValue, !!offer.shouldDisplayValue)

  const guestsLabel = useMemo(() => rooms && getOccupancyDescription(rooms, false), [rooms])
  const checkinLabel = useMemo(() => {
    if (!checkIn) return
    const checkOut = checkIn.clone().add(duration, 'days')
    const dateLabel = `${checkIn.format('DD')} - ${checkOut.format('DD MMM')}`
    return dateLabel
  }, [checkIn, duration])

  const handleOpenCapacity = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const handleOpenCalendar = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch])

  return (
    <OfferPageStickyFooter visible callToActionDisabled >
      <Group direction="vertical" gap={4}>
        <Group direction="horizontal" gap={4} noDisplayWhenEmpty>
          {rooms && (
            <FilterChip size="small" onClick={handleOpenCapacity}>
              {guestsLabel}
            </FilterChip>
          )}
          {checkIn && (
            <FilterChip size="small" onClick={handleOpenCalendar}>
              {checkinLabel}
            </FilterChip>
          )}
        </Group>
        <div>
          <PriceRowCaption>
            {pluralizeToString('night', lowestPricePackage.duration)} from
          </PriceRowCaption>
          <PriceRowPrice
            size="M"
            price={pricePackage.totalPrice}
            saleUnit={pricePackage.saleUnit}
          />
          {shouldShowValue && shouldShowRate && <PriceRowValueDiscountWithCaption
            size="L"
            originalValue={Math.ceil(pricePackage.totalValue)}
            discountPercentage={pricePackage.valueOffPercent}
          />}
        </div>
      </Group>
      <FlashHotelAvailabilityButton/>
    </OfferPageStickyFooter>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: appState.offer,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
}))(HotelOfferPageStickyFooterVariant)
